import type {
  AcceptRejectRequest,
  AISegmentSuggestionsRequest,
  AISegmentSuggestionsResponse,
  BatchDeleteResponse,
  CollectorsResponse,
  DatasetLabelJobResponse,
  DatasetLabelRequest,
  DatasetPendingResponse,
  DesktopDownloadResponse,
  IndexResponse,
  JobStatusResponse,
  PresignedOperation,
  PresignedUrlsResponse,
  ThumbnailBatchResponse,
  UploadCredentialsResponse,
} from './types';
import { getBastionApiBaseUrl } from './env';

export class BastionApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly bodyText?: string
  ) {
    super(message);
    this.name = 'BastionApiError';
  }
}

export interface BastionApiClientOptions {
  baseUrl?: string;
}

export class BastionApiClient {
  readonly baseUrl: string;

  constructor(opts: BastionApiClientOptions = {}) {
    this.baseUrl = (opts.baseUrl || getBastionApiBaseUrl()).replace(/\/+$/, '');
  }

  private async requestJson<T>(
    path: string,
    {
      method,
      token,
      body,
    }: { method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; token: string; body?: unknown }
  ): Promise<T> {
    const resp = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new BastionApiError(
        text
          ? `Bastion API error (${method} ${path}): ${resp.status} — ${text}`
          : `Bastion API error (${method} ${path}): ${resp.status}`,
        resp.status,
        text
      );
    }

    if (resp.status === 204) {
      return {} as T;
    }
    return (await resp.json()) as T;
  }

  async getUploadCredentials(episodeId: string, token: string): Promise<UploadCredentialsResponse> {
    // NOTE: server endpoint is /episodes/upload-credentials
    return this.requestJson<UploadCredentialsResponse>('/episodes/upload-credentials', {
      method: 'POST',
      token,
      body: { episode_id: episodeId },
    });
  }

  async getPresignedUrls(
    episodeId: string,
    files: string[],
    operation: PresignedOperation,
    token: string
  ): Promise<PresignedUrlsResponse> {
    return this.requestJson<PresignedUrlsResponse>('/episodes/presigned-urls', {
      method: 'POST',
      token,
      body: { episode_id: episodeId, files, operation },
    });
  }

  async updateMetadata(episodeId: string, metadata: Record<string, unknown>, token: string): Promise<void> {
    await this.requestJson<{ success: boolean }>(`/episodes/${encodeURIComponent(episodeId)}/metadata`, {
      method: 'PUT',
      token,
      body: { metadata },
    });
  }

  /**
   * Index an episode in Supabase after uploading to S3.
   * 
   * @param episodeId - The episode ID to index
   * @param token - Supabase JWT token
   * @param attribution - Optional attribution data (collector_id, reviewer_id, reviewed_at)
   */
  async indexEpisode(
    episodeId: string,
    token: string,
    attribution?: {
      collector_id?: string | null;
      reviewer_id?: string | null;
      reviewed_at?: string | null;
    }
  ): Promise<IndexResponse> {
    return this.requestJson<IndexResponse>(`/episodes/${encodeURIComponent(episodeId)}/index`, {
      method: 'POST',
      token,
      body: attribution ? { attribution } : undefined,
    });
  }

  /**
   * Get presigned download URLs for episode thumbnails in bulk.
   *
   * Returns a map of episode ID to presigned thumbnail URL.
   * Only episodes that have a thumbnail in S3 are included.
   */
  async getThumbnailUrls(
    episodeIds: string[],
    token: string
  ): Promise<Record<string, string>> {
    const resp = await this.requestJson<ThumbnailBatchResponse>('/episodes/thumbnails/batch', {
      method: 'POST',
      token,
      body: { episode_ids: episodeIds },
    });
    return resp.urls;
  }

  /**
   * Update the task description for an episode (S3 metadata.json + Supabase row).
   */
  async updateTask(
    episodeId: string,
    description: string,
    token: string
  ): Promise<{ success: boolean; episode_id: string }> {
    return this.requestJson<{ success: boolean; episode_id: string }>(
      `/episodes/${encodeURIComponent(episodeId)}/task`,
      { method: 'PUT', token, body: { description } }
    );
  }

  /**
   * Partially update editable episode fields (task_rating, collector_id,
   * collector_notes, review notes, tags, live SOP context).
   * Both S3 metadata.json and the Supabase row are updated server-side.
   */
  async patchEpisode(
    episodeId: string,
    updates: {
      task_rating?: number | null;
      collector_id?: string | null;
      collector_notes?: string | null;
      notes?: string | null;
      tags?: string[] | null;
      sop_id?: string | null;
      sop_version?: number | null;
    },
    token: string
  ): Promise<{ success: boolean; episode_id: string }> {
    return this.requestJson<{ success: boolean; episode_id: string }>(
      `/episodes/${encodeURIComponent(episodeId)}`,
      { method: 'PATCH', token, body: updates }
    );
  }

  /**
   * Delete an episode from S3 and Supabase.
   */
  async deleteEpisode(episodeId: string, token: string): Promise<{ success: boolean; episode_id: string }> {
    return this.requestJson<{ success: boolean; episode_id: string }>(
      `/episodes/${encodeURIComponent(episodeId)}`,
      { method: 'DELETE', token }
    );
  }

  /**
   * List collectors (id + email) for the filter dropdown.
   * Resolved server-side via Supabase admin API.
   */
  async getCollectors(token: string): Promise<CollectorsResponse['collectors']> {
    const resp = await this.requestJson<CollectorsResponse>('/episodes/collectors', {
      method: 'GET',
      token,
    });
    return resp.collectors;
  }

  /**
   * Update task description for multiple episodes at once.
   */
  async batchUpdateTask(
    episodeIds: string[],
    taskDescription: string,
    token: string
  ): Promise<{ updated: number; failed: number }> {
    return this.requestJson<{ updated: number; failed: number }>('/episodes/batch/task', {
      method: 'PUT',
      token,
      body: { episode_ids: episodeIds, task_description: taskDescription },
    });
  }

  /**
   * Start a background job to delete multiple episodes.
   * Poll getJobStatus(jobId) for progress.
   */
  async submitBatchDelete(
    episodeIds: string[],
    token: string
  ): Promise<BatchDeleteResponse['job_id']> {
    const resp = await this.requestJson<BatchDeleteResponse>('/episodes/batch/delete', {
      method: 'POST',
      token,
      body: { episode_ids: episodeIds },
    });
    return resp.job_id;
  }

  /**
   * Get status of a batch job.
   */
  async getJobStatus(jobId: string, token: string): Promise<JobStatusResponse> {
    return this.requestJson<JobStatusResponse>(`/episodes/jobs/${encodeURIComponent(jobId)}`, {
      method: 'GET',
      token,
    });
  }

  /**
   * Get a presigned download URL for the desktop installer.
   * @param platform - Target platform (e.g., "macos", "linux", "linux-deb", "linux-appimage")
   */
  async getDesktopDownload(platform: string, token: string): Promise<DesktopDownloadResponse> {
    return this.requestJson<DesktopDownloadResponse>(
      `/desktop/download?platform=${encodeURIComponent(platform)}`,
      { method: 'GET', token }
    );
  }

  /**
   * Start an async AI segment suggestion job for an episode.
   * Returns immediately — poll with `getAISegmentSuggestions` for results.
   */
  async startAISegmentSuggestions(
    episodeId: string,
    request: AISegmentSuggestionsRequest,
    token: string,
  ): Promise<AISegmentSuggestionsResponse> {
    return this.requestJson<AISegmentSuggestionsResponse>(
      `/episodes/${encodeURIComponent(episodeId)}/ai-segment-suggestions`,
      { method: 'POST', token, body: request },
    );
  }

  /**
   * Poll for AI segment suggestion results.
   * Returns status: idle | processing | completed | failed.
   */
  async getAISegmentSuggestions(
    episodeId: string,
    token: string,
  ): Promise<AISegmentSuggestionsResponse> {
    return this.requestJson<AISegmentSuggestionsResponse>(
      `/episodes/${encodeURIComponent(episodeId)}/ai-segment-suggestions`,
      { method: 'GET', token },
    );
  }

  // ── Dataset AI Labeling ───────────────────────────────────────────────────

  async startDatasetAILabel(
    datasetId: string,
    request: DatasetLabelRequest,
    token: string,
  ): Promise<DatasetLabelJobResponse> {
    return this.requestJson<DatasetLabelJobResponse>(
      `/datasets/${encodeURIComponent(datasetId)}/ai-label`,
      { method: 'POST', token, body: request },
    );
  }

  async getDatasetAILabelStatus(
    datasetId: string,
    jobId: string,
    token: string,
  ): Promise<DatasetLabelJobResponse> {
    return this.requestJson<DatasetLabelJobResponse>(
      `/datasets/${encodeURIComponent(datasetId)}/ai-label/${encodeURIComponent(jobId)}`,
      { method: 'GET', token },
    );
  }

  async cancelDatasetAILabel(
    datasetId: string,
    jobId: string,
    token: string,
  ): Promise<{ success: boolean }> {
    return this.requestJson<{ success: boolean }>(
      `/datasets/${encodeURIComponent(datasetId)}/ai-label/${encodeURIComponent(jobId)}`,
      { method: 'DELETE', token },
    );
  }

  async acceptAILabels(
    datasetId: string,
    body: AcceptRejectRequest,
    token: string,
  ): Promise<{ accepted: number }> {
    return this.requestJson<{ accepted: number }>(
      `/datasets/${encodeURIComponent(datasetId)}/ai-label/accept`,
      { method: 'POST', token, body },
    );
  }

  async rejectAILabels(
    datasetId: string,
    body: AcceptRejectRequest,
    token: string,
  ): Promise<{ rejected: number }> {
    return this.requestJson<{ rejected: number }>(
      `/datasets/${encodeURIComponent(datasetId)}/ai-label/reject`,
      { method: 'POST', token, body },
    );
  }

  async getActiveDatasetAILabel(
    datasetId: string,
    token: string,
  ): Promise<DatasetLabelJobResponse | null> {
    const resp = await this.requestJson<DatasetLabelJobResponse & { job_id: string | null }>(
      `/datasets/${encodeURIComponent(datasetId)}/ai-label/active`,
      { method: 'GET', token },
    );
    if (!resp.job_id) return null;
    return resp;
  }

  async getDatasetPendingLabels(
    datasetId: string,
    token: string,
  ): Promise<DatasetPendingResponse> {
    return this.requestJson<DatasetPendingResponse>(
      `/datasets/${encodeURIComponent(datasetId)}/ai-label/pending`,
      { method: 'GET', token },
    );
  }

  // ── Per-Episode AI Segment Accept / Reject ────────────────────────────────

  async acceptEpisodeAISegments(
    episodeId: string,
    token: string,
  ): Promise<{ accepted: number; episode_id: string }> {
    return this.requestJson<{ accepted: number; episode_id: string }>(
      `/episodes/${encodeURIComponent(episodeId)}/ai-segments/accept`,
      { method: 'POST', token },
    );
  }

  async rejectEpisodeAISegments(
    episodeId: string,
    token: string,
  ): Promise<{ rejected: number; episode_id: string }> {
    return this.requestJson<{ rejected: number; episode_id: string }>(
      `/episodes/${encodeURIComponent(episodeId)}/ai-segments/reject`,
      { method: 'POST', token },
    );
  }
}

